// src/modules/dashboard/components/RecentActivity.tsx

import React from 'react';
import Card from '../../../shared/components/ui/Card';
import { User } from '../../../types/user';
import { SecurityLog } from '../../../types/user';
import './RecentActivity.css';

interface ActivityItem {
  id: string;
  type: 'login' | 'profile' | 'system' | 'security';
  title: string;
  description: string;
  timestamp: Date;
  icon: string;
  color: string;
}

interface RecentActivityProps {
  user: User;
  securityLogs?: SecurityLog[];
}

const RecentActivity: React.FC<RecentActivityProps> = ({ user, securityLogs }) => {
  // Convertir logs de seguridad reales en actividades
  const generateActivitiesFromLogs = (logs: SecurityLog[]): ActivityItem[] => {
    return logs.map((log, index) => {
      const getActivityInfo = (action: string) => {
        const actionMap: Record<string, { title: string; icon: string; color: string; type: ActivityItem['type'] }> = {
          'login': { title: 'Inicio de sesión', icon: '🔓', color: 'var(--success-color)', type: 'login' },
          'logout': { title: 'Cierre de sesión', icon: '🚪', color: 'var(--info-color)', type: 'login' },
          'profile_updated': { title: 'Perfil actualizado', icon: '✏️', color: 'var(--warning-color)', type: 'profile' },
          'password_changed': { title: 'Contraseña cambiada', icon: '🔑', color: 'var(--danger-color)', type: 'security' },
          'email_verified': { title: 'Email verificado', icon: '✅', color: 'var(--success-color)', type: 'security' },
          'account_created': { title: 'Cuenta creada', icon: '👤', color: 'var(--primary-color)', type: 'system' },
          'token_refreshed': { title: 'Sesión renovada', icon: '🔄', color: 'var(--info-color)', type: 'security' },
          'google_auth': { title: 'Autenticación con Google', icon: '🔐', color: 'var(--success-color)', type: 'login' }
        };
        
        return actionMap[action] || { 
          title: action.replace('_', ' '), 
          icon: '📋', 
          color: 'var(--primary-color)', 
          type: 'system' as const 
        };
      };

      const activityInfo = getActivityInfo(log.action);
      
      // Extraer información útil del user_agent
      const getBrowserInfo = (userAgent: string | undefined) => {
        if (!userAgent) return 'Navegador desconocido';
        
        if (userAgent.includes('Chrome')) return 'Chrome';
        if (userAgent.includes('Firefox')) return 'Firefox';
        if (userAgent.includes('Safari')) return 'Safari';
        if (userAgent.includes('Edge')) return 'Edge';
        return userAgent.split(' ')[0] || 'Navegador desconocido';
      };
      
      return {
        id: `log-${index}`,
        type: activityInfo.type,
        title: activityInfo.title,
        description: `${getBrowserInfo(log.user_agent)} - IP: ${log.ip_address}`,
        timestamp: new Date(log.created_at),
        icon: activityInfo.icon,
        color: activityInfo.color
      };
    });
  };

  // Solo usar logs reales de la base de datos
  const activities = securityLogs && securityLogs.length > 0 
    ? generateActivitiesFromLogs(securityLogs)
    : [];

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Ahora mismo';
    } else if (diffMinutes < 60) {
      return `Hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    } else {
      return timestamp.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: timestamp.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getActivityTypeClass = (type: ActivityItem['type']) => {
    switch (type) {
      case 'login': return 'activity-login';
      case 'profile': return 'activity-profile';
      case 'security': return 'activity-security';
      case 'system': return 'activity-system';
      default: return 'activity-default';
    }
  };

  return (
    <Card className="recent-activity-container" variant="glass" padding="large">
      <div className="activity-header">
        <h2 className="section-title">Actividad Reciente</h2>
        <p className="section-subtitle">
          {securityLogs && securityLogs.length > 0
            ? `${securityLogs.length} registros de seguridad de la base de datos`
            : 'No hay registros de actividad disponibles en la base de datos'
          }
        </p>
      </div>
      
      <div className="activity-list">
        {activities.length > 0 ? (
          activities.slice(0, 10).map((activity, index) => (
            <div 
              key={activity.id} 
              className={`activity-item ${getActivityTypeClass(activity.type)} activity-item-${index}`}
            >
              <div 
                className="activity-icon"
                style={{ backgroundColor: activity.color }}
              >
                {activity.icon}
              </div>
              
              <div className="activity-content">
                <div className="activity-main">
                  <h4 className="activity-title">{activity.title}</h4>
                  <p className="activity-description">{activity.description}</p>
                </div>
                
                <div className="activity-meta">
                  <time className="activity-time" dateTime={activity.timestamp.toISOString()}>
                    {formatTimestamp(activity.timestamp)}
                  </time>
                </div>
              </div>
              
              <div className="activity-status">
                <div className="status-dot" style={{ backgroundColor: activity.color }}></div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-activities">
            <div className="no-activities-icon">📋</div>
            <h3>Sin actividad registrada</h3>
            <p>
              Los registros de actividad aparecerán aquí cuando:
            </p>
            <ul className="activity-requirements">
              <li>Inicies o cierres sesión</li>
              <li>Actualices tu perfil</li>
              <li>Realices cambios de seguridad</li>
              <li>Interactúes con la plataforma</li>
            </ul>
          </div>
        )}
      </div>
      
      <div className="activity-footer">
        <button className="view-all-btn" disabled>
          <span>Ver toda la actividad</span>
          <span className="btn-arrow">→</span>
        </button>
        <small className="footer-note">
          {securityLogs && securityLogs.length > 0
            ? 'Datos obtenidos directamente de los logs de seguridad'
            : 'Los registros de actividad se almacenan automáticamente en la base de datos'
          }
        </small>
      </div>
    </Card>
  );
};

export default RecentActivity;